/**
 * CADENCE SDK — CDN Entry Point
 * This file creates a global `Cadence` facade for use via <script> tags.
 *
 * Zero-code usage (recommended for non-technical users):
 *   <script src="https://cdn.softpath.co/sdk/v1/cadence.min.js" data-project="proj_abc123"></script>
 *
 * Developer usage:
 *   <script src="https://cdn.softpath.co/sdk/v1/cadence.min.js"></script>
 *   <script>
 *     Cadence.init({ apiKey: 'proj_abc123', userId: 'user_1' });
 *     Cadence.runTest({ ... });
 *   </script>
 */

import { CadenceClient } from './index'

// Capture currentScript immediately at parse time — it becomes null after synchronous execution completes
const _currentScript = (typeof document !== 'undefined'
  ? document.currentScript as HTMLScriptElement | null
  : null) || (typeof document !== 'undefined'
  ? document.querySelector('script[src*="cadence.min.js"]') as HTMLScriptElement | null
  : null);

interface CadenceInitConfig {
  apiKey: string
  userId?: string
  apiUrl?: string
  attributes?: Record<string, unknown>
  autoDiscover?: boolean
}

interface RunTestVariant {
  name: string
  weight: number
  apply: (element: Element | null) => void
}

interface RunTestConfig {
  testId: string
  urlPattern?: RegExp | string
  selector?: string
  variants: RunTestVariant[]
}

let client: CadenceClient | null = null

function ensureInitialized(): CadenceClient {
  if (!client) {
    throw new Error('[CADENCE] SDK not initialized. Call Cadence.init() or use data-project attribute.')
  }
  return client
}

const Cadence = {
  version: '1.0.0',

  /**
   * Initialize the CADENCE SDK
   */
  init(config: CadenceInitConfig): void {
    if (client) {
      console.warn('[CADENCE] SDK already initialized. Ignoring duplicate init() call.')
      return
    }

    const apiKey = config.apiKey
    const isPublicId = apiKey.startsWith('proj_')

    client = new CadenceClient({
      ...(isPublicId ? { projectId: apiKey } : { sdkKey: apiKey }),
      userId: config.userId,
      apiUrl: config.apiUrl,
      attributes: config.attributes,
      // Auto-run visual experiments when using public project ID
      autoRun: isPublicId,
      autoDiscover: config.autoDiscover,
    })
  },

  /**
   * Wait for the SDK to be ready
   */
  async ready(): Promise<void> {
    return ensureInitialized().ready()
  },

  /**
   * Run a test with declarative config and code-based variant application
   */
  async runTest(config: RunTestConfig): Promise<string> {
    const sdk = ensureInitialized()
    await sdk.ready()

    // Check URL pattern match
    if (config.urlPattern) {
      const currentUrl = window.location.pathname + window.location.search
      const pattern = config.urlPattern instanceof RegExp
        ? config.urlPattern
        : new RegExp(config.urlPattern)

      if (!pattern.test(currentUrl)) {
        return 'control'
      }
    }

    // Get variant assignment
    const variantName = sdk.getVariant(config.testId)

    // Find the matching variant config
    const variantConfig = config.variants.find(v => v.name === variantName)
    if (!variantConfig) {
      return variantName
    }

    // Find element if selector provided
    let element: Element | null = null
    if (config.selector) {
      element = document.querySelector(config.selector)
    }

    // Apply variant changes
    try {
      variantConfig.apply(element)
    } catch (err) {
      console.error(`[CADENCE] Error applying variant "${variantName}":`, err)
    }

    return variantName
  },

  /**
   * Get the assigned variant for an experiment
   */
  getVariant(experimentName: string): string {
    return ensureInitialized().getVariant(experimentName)
  },

  /**
   * Track a custom event
   */
  track(eventName: string, properties?: Record<string, unknown>): void {
    ensureInitialized().track(eventName, properties)
  },

  /**
   * Track a conversion event
   */
  trackConversion(conversionName: string, properties?: Record<string, unknown>): void {
    ensureInitialized().trackConversion(conversionName, properties)
  },

  /**
   * Check if a feature flag is enabled
   */
  isFeatureEnabled(featureKey: string): boolean {
    return ensureInitialized().isFeatureEnabled(featureKey)
  },

  /**
   * Get a feature flag value
   */
  getFeatureValue(featureKey: string, defaultValue?: unknown): unknown {
    return ensureInitialized().getFeatureValue(featureKey, defaultValue)
  },

  /**
   * Enable anti-flicker protection
   */
  enableAntiFlicker(timeout?: number): void {
    ensureInitialized().enableAntiFlicker(timeout)
  },

  /**
   * Destroy the SDK and flush remaining events
   */
  destroy(): void {
    if (client) {
      client.destroy()
      client = null
    }
  },
}

// Expose as global for IIFE builds
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).Cadence = Cadence
}

// ==================== AUTO-INIT FROM SCRIPT TAG ====================
// Detects data-project attribute on the script tag and auto-initializes.
// Usage: <script src="cadence.min.js" data-project="proj_abc123"></script>

function autoInit(): void {
  if (typeof document === 'undefined') return

  // Use the captured currentScript (grabbed at parse time before it goes null)
  let scriptTag = _currentScript

  // Fallback: find script tag with data-project attribute
  if (!scriptTag || !scriptTag.getAttribute('data-project')) {
    const scripts = document.querySelectorAll('script[data-project]')
    if (scripts.length > 0) {
      scriptTag = scripts[scripts.length - 1] as HTMLScriptElement
    }
  }

  if (!scriptTag) return

  const projectId = scriptTag.getAttribute('data-project')
  if (!projectId) return

  // Read optional attributes
  const apiUrl = scriptTag.getAttribute('data-api-url') || undefined
  const userId = scriptTag.getAttribute('data-user-id') || undefined
  const antiFlicker = scriptTag.hasAttribute('data-anti-flicker')

  const autoDiscover = scriptTag.hasAttribute('data-auto-discover')

  // Initialize with project ID (auto-run enabled by default)
  Cadence.init({
    apiKey: projectId,
    apiUrl,
    userId,
    autoDiscover,
  })

  // Enable anti-flicker if requested
  if (antiFlicker) {
    const timeout = parseInt(scriptTag.getAttribute('data-anti-flicker-timeout') || '2000', 10)
    Cadence.enableAntiFlicker(timeout)
  }
}

// ==================== AUTO-INIT FROM window.CADENCE_CONFIG ====================
// Allows initialization via a config object set before the script loads.
// Usage:
//   <script>window.CADENCE_CONFIG = { projectId: 'proj_xxx', autoDiscover: true, autoRun: true };</script>
//   <script src="cadence.min.js" async></script>

interface CadenceWindowConfig {
  projectId: string
  apiUrl?: string
  userId?: string
  autoDiscover?: boolean
}

function autoInitFromConfig(): void {
  if (typeof window === 'undefined') return
  const config = (window as unknown as { CADENCE_CONFIG?: CadenceWindowConfig }).CADENCE_CONFIG
  if (!config || !config.projectId) return
  if (client) return

  Cadence.init({
    apiKey: config.projectId,
    apiUrl: config.apiUrl,
    userId: config.userId,
    autoDiscover: config.autoDiscover,
  })
}

// Run auto-init immediately (check CADENCE_CONFIG first, then script tag)
autoInitFromConfig()
autoInit()

export default Cadence
